/**
 * Ablauf der Anmeldung im Browser prüfen, ohne dass daraus eine Schleife werden kann.
 *
 * Fehler vom 03.11.2026 (Kundin „Großmann“, dazu zwei Konten „Voß“): `useCurrentUser` und `AccessContext` lasen das
 * Token mit `atob()`. Ein JWT ist aber in **base64url** geschrieben (`-` und `_` statt `+` und `/`). Steht im Namen ein
 * „o“ direkt vor „ß“ (oder ein ähnliches Paar), enthält das Token ein `_`, `atob()` wirft, und der Code hielt das Token
 * für kaputt: `refreshSession()` → `TOKEN_REFRESHED` → `useAuthSync` lädt `current-user` neu → wieder `atob()` → wieder
 * erneuern. Rund zehnmal je Sekunde, bis Supabase nach etwa 30 Erneuerungen mit 429 antwortet; auth-js wertet 429 als
 * endgültigen Fehler, löscht die Sitzung (`SIGNED_OUT`) und das Mitglied steht wieder auf der Anmeldeseite.
 *
 * Deshalb hier: das Token richtig lesen (base64url, UTF-8) und eine Bremse, damit eigener Code höchstens einmal je
 * Minute selbst erneuert. Supabase erneuert ohnehin von selbst (`getSession()` 90 Sekunden vor Ablauf, dazu der
 * Hintergrund-Takt); unsere Prüfung ist nur das Netz darunter.
 */

/** Nutzlast eines JWT lesen (base64url oder base64, Text als UTF-8). `null`, wenn es sich nicht lesen lässt. */
export function tokenNutzlast(token: string | null | undefined): Record<string, unknown> | null {
  const teil = typeof token === "string" ? token.split(".")[1] : undefined;
  if (!teil) return null;
  try {
    let b64 = teil.replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4 !== 0) b64 += "=";
    const binaer = atob(b64);
    const bytes = Uint8Array.from(binaer, (zeichen) => zeichen.charCodeAt(0));
    const wert: unknown = JSON.parse(new TextDecoder().decode(bytes));
    return wert && typeof wert === "object" && !Array.isArray(wert) ? (wert as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/** Ablaufzeit in Millisekunden: `exp` aus dem Token, sonst `expires_at` der Sitzung; `null`, wenn beides fehlt. */
export function ablaufMs(sitzung: { access_token?: string | null; expires_at?: number | null }): number | null {
  const exp = tokenNutzlast(sitzung.access_token)?.exp;
  if (typeof exp === "number" && Number.isFinite(exp)) return exp * 1000;
  if (typeof sitzung.expires_at === "number" && Number.isFinite(sitzung.expires_at)) return sitzung.expires_at * 1000;
  return null;
}

/** Puffer vor dem Ablauf, ab dem unser Code selbst erneuert (wie bisher in `useCurrentUser`). */
export const ABLAUF_PUFFER_MS = 30_000;

/**
 * `true`: läuft in weniger als 30 Sekunden ab. `false`: gültig. `null`: Ablauf unbekannt; dann **nicht** selbst
 * erneuern (genau das war die Schleife), Supabase kümmert sich.
 */
export function laeuftBaldAb(
  sitzung: { access_token?: string | null; expires_at?: number | null },
  jetzt: number = Date.now(),
  puffer: number = ABLAUF_PUFFER_MS,
): boolean | null {
  const ablauf = ablaufMs(sitzung);
  if (ablauf === null) return null;
  return ablauf - jetzt < puffer;
}

/** Bremse: Eigener Code ruft `refreshSession()` höchstens einmal in diesem Abstand (auch bei falsch gehender Uhr). */
export const SELBST_ERNEUERN_ABSTAND_MS = 60_000;

let zuletztSelbstErneuert = Number.NEGATIVE_INFINITY;

/** Darf eigener Code jetzt `refreshSession()` rufen? Merkt sich den Zeitpunkt, wenn ja. */
export function darfSelbstErneuern(jetzt: number = Date.now()): boolean {
  if (jetzt - zuletztSelbstErneuert < SELBST_ERNEUERN_ABSTAND_MS) return false;
  zuletztSelbstErneuert = jetzt;
  return true;
}

/** Nur für Tests: Bremse lösen. */
export function selbstErneuernZuruecksetzen(): void {
  zuletztSelbstErneuert = Number.NEGATIVE_INFINITY;
}
